/**
 * UserContext — stores the authenticated user's DB profile and
 * an optional "view as role" override for admin testing.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { usersApi } from '../services/api';

export interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  role: string;
  department?: string | null;
  job_title?: string | null;
  is_active: number;
  is_approved: number;
}

interface UserContextValue {
  user: UserProfile | null;
  /** The role admin has chosen to preview as, or null when not impersonating */
  viewAsRole: string | null;
  /** effectiveRole = viewAsRole ?? user.role — use this for all UI decisions */
  effectiveRole: string;
  /** True only when the *actual* DB role is admin */
  isAdmin: boolean;
  setViewAsRole: (role: string | null) => void;
  loading: boolean;
  refresh: () => void;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  viewAsRole: null,
  effectiveRole: 'viewer',
  isAdmin: false,
  setViewAsRole: () => {},
  loading: true,
  refresh: () => {}
});

export function UserProvider({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [viewAsRole, setViewAsRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    usersApi.me()
      .then(res => {
        setUser(res.data);
        // Clear any view-as override if the real role changed
        setViewAsRole(prev => (res.data.role !== 'admin' ? null : prev));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAuthenticated, tick]);

  const effectiveRole = viewAsRole ?? user?.role ?? 'viewer';
  const isAdmin = user?.role === 'admin';

  const handleSetViewAsRole = (role: string | null) => {
    if (!isAdmin) return; // safety guard
    setViewAsRole(role);
  };

  return (
    <UserContext.Provider value={{
      user,
      viewAsRole,
      effectiveRole,
      isAdmin,
      setViewAsRole: handleSetViewAsRole,
      loading,
      refresh: () => setTick(t => t + 1)
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
